import React, { useState } from "react";
import DataTable from "Common/DataTable/DataTable";
import FormDatePicker from "Common/Custom/ReactFlatpickr";
import FormSelect from "Common/Custom/FormSelect";
import useDebouncedValue from "Common/DataTable/useDebouncedValue";
import usePaginatedFetch from "Hooks/usePaginatedFetch";
import useTableConfig from "Hooks/useTableConfig";
import useFetchAllData from "Hooks/useFetchAllData";
import { generateDisplayFields } from "utils/fieldMapper";
import fields from "../../json/LoanReportFields.json";
import {
    getLoanReportsWithPagination,
    GetReportEmployeeList
} from "adminApi/LoanReportApi/LoanReportApi.js";

const LoanReportListTable = () => {
    const [search, setSearch] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [employeeId, setEmployeeId] = useState("");
    const [filters, setFilters] = useState({ from_date: "", to_date: "", employee_id: "" });

    const [limit, setLimit] = useState(10);
    const [page, setPage] = useState(1);
    const debouncedSearch = useDebouncedValue(search, 500);
    const sizeOptions = [10, 20, 50, 100];
    const { tabledata, formdata } = fields;

    // Fetch employees for dropdown
    const { data: employeeOptions, loading: loadingEmployees } = useFetchAllData(GetReportEmployeeList);

    // Fetch Hook
    const { data, meta, loading, error, refetch } = usePaginatedFetch(getLoanReportsWithPagination, {
        page,
        limit,
        search: debouncedSearch,
        from_date: filters.from_date,
        to_date: filters.to_date,
        employee_id: filters.employee_id,
        date_column: "approve_date",
        title: "Loan Report",
    });

    const displayFields = generateDisplayFields(tabledata);

    // Table Hook
    const { formattedData, columns } = useTableConfig({
        data,
        page,
        limit,
        canShowAction: true,
        refetch,
        isPrint: false,
        IsDelete: false,
        IsEdit: false,
        isView: true,
        config: {
            entityName: "Loan Report",
            idKey: "employee_id",
            displayFields,
            ViewUrl: "view-employee"
        },
    });

    const handleSearch = (val, resetPage = null) => {
        setSearch(val);
        if (resetPage !== null) setPage(resetPage);
    };

    const handleDateChange = (e) => {
        const { name, value } = e.target;
        if (name === "from_date") setFromDate(value);
        if (name === "to_date") setToDate(value);
    };

    const handleFilterSearch = () => {
        setFilters({ from_date: fromDate, to_date: toDate, employee_id: employeeId });
        setPage(1);
    };

    const handleClearFilters = () => {
        setFromDate("");
        setToDate("");
        setEmployeeId("");
        setFilters({ from_date: "", to_date: "", employee_id: "" });
        setPage(1);
    };

    const onPageChange = (p) => setPage(p);
    const onLimitChange = (newLimit) => {
        setLimit(newLimit);
        setPage(1);
    };

    const employeeSelectOptions = employeeOptions?.map(emp => ({
        value: emp.employee_id,
        label: emp.employee_name
    }));

    return (
        <>
            <div className="card mb-3">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-2">Search Report</h5>
                </div>
                <div className="card-body">
                    <div className="row g-3 align-items-end">
                        <FormSelect
                            field={formdata[0]}
                            value={employeeId}
                            handleChange={(e) => setEmployeeId(e.target.value)}
                            options={employeeSelectOptions}
                        />
                        <FormDatePicker
                            field={{
                                name: "from_date",
                                label: "From",
                                placeholder: "Select From Date",
                                fieldSize: "col-sm-auto col-12"
                            }}
                            value={fromDate}
                            handleChange={handleDateChange}
                        />
                        <FormDatePicker
                            field={{
                                name: "to_date",
                                label: "To",
                                placeholder: "Select To Date",
                                fieldSize: "col-sm-auto col-12"
                            }}
                            value={toDate}
                            handleChange={handleDateChange}
                        />
                        <div className="col-sm-auto col-12 d-flex gap-2">
                            <button
                                className="btn btn-primary"
                                onClick={handleFilterSearch}
                            >
                                <i className="fa fa-search me-1"></i> Search
                            </button>
                            <button
                                className="btn btn-outline-secondary"
                                onClick={handleClearFilters}
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <DataTable
                columns={columns}
                data={formattedData}
                options={{
                    loading: loading || loadingEmployees,
                    error,
                    sortableColumns: displayFields.map((f) => f.accessor),
                    copy: true,
                    csv: true,
                    pdf: true,
                    print: true,
                }}
                Search={{
                    search: true,
                    SearchInputValue: search,
                    handleSearch: handleSearch,
                }}
                Entries={{
                    Entries: true,
                    limit,
                    onLimitChange,
                    options: sizeOptions,
                }}
                pagination={{
                    isActive: true,
                    meta,
                    onPageChange,
                }}
            />
        </>
    );
};

export default LoanReportListTable;
